import { useEffect, useRef, useState } from "react";
import { cloneDeep, isArray, isEmpty, isEqual, map, range } from "lodash";
import { FieldArray, Formik, useField, useFormik, useFormikContext } from "formik";
import GoalTrackButton from "./GoalTrackButton";
import { Accordion, Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import { GOAL_TRACK_DEFAULT_VALUES_V2, GOAL_TRACK_PARAMETERS_COLORS_V2, GOAL_TRACK_PARAMETERS_V2 } from "config/constants";
import SessionOutcomePieChart from "components/shared/ReportComponents/SessionOutcomePieChart";
import SessionTrackingButton from "components/shared/ReportsV2/TaskPlanSessions/SessionTrackingButton";
import { getGoalTrackingPercentage, isTrialTrackerEmpty } from "helpers/assessment";
import { MinusBlackIcon, PlusBlackIcon } from "Assets/icons";
import { RequiredFieldAstrick } from "components/Common/Common";
import GoalTrackingWatcher from "./GoalTrackingWatcher";
import threDots_img from "Assets/images/three-dots.png";
import CustomEditor from "components/TipTapEditor/CustomeEditor";
import confirmDialogV2 from "utils/confirmationDialogV2";
import { useAutosaveReportContext } from "Context/AutosaveReportContext";

const SCORE_TRACKER_CLIENT_TYPES = ["school", "home_education", "private"];

// `scores` ({ O, M, P, SC, I }) as pie chart rows.
const scoresToTrackingRows = (scores) => Object.keys(GOAL_TRACK_PARAMETERS_V2).map((tracked) => ({
  tracked,
  count: Number(scores?.[tracked]) || 0,
}));

const SchoolTrialTracker = ({ goal, values, setFieldValue, saveGoalTrackings, goalTrackLoading }) => {
  const trackings = values?.goalTracking || [];
  const scoreRows = scoresToTrackingRows(values?.scores);
  const getCount = (parameter) => scoreRows.find((row) => row.tracked === parameter)?.count || 0;
  const totalTrials = Object.keys(GOAL_TRACK_PARAMETERS_V2).reduce((sum, parameter) => sum + getCount(parameter), 0);

  const setTrackingCount = (parameter, value) => {
    const targetCount = Number(value) || 0;
    const otherTrackings = trackings.filter((tracking) => tracking.tracked !== parameter);
    const currentRows = trackings.filter((tracking) => tracking.tracked === parameter);
    const currentCount = currentRows.reduce((sum, tracking) => sum + (tracking.count === undefined ? 1 : Number(tracking.count) || 0), 0);

    if (targetCount === 0 && currentCount === 0) return;

    setFieldValue("goalTracking", [
      ...otherTrackings,
      {
        ...(currentRows[0] || {}),
        assesment_goal: goal.id,
        tracked: parameter,
        count: targetCount,
        dirty: targetCount !== currentCount || currentRows.some((tracking) => tracking.dirty),
      },
    ]);
    setFieldValue("hasUnsavedChanges", targetCount !== currentCount || currentRows.some((tracking) => tracking.dirty));
  };

  return (
    <Row>
      <Col xs="4" className="my-2">
        <div className="cardCstm rounded-3">
          <div className="contentBody">
            <Row>
              <Col lg="12" className="my-2">
                <SessionOutcomePieChart trackingData={scoreRows} version={2} />
              </Col>
              <Col lg="12" className="my-2">
                <ul className="list-unstyled ps-0 mb-0" style={{ maxWidth: "239px" }}>
                  {Object.entries(GOAL_TRACK_PARAMETERS_V2).map(([parameter, label]) => {
                    const count = getCount(parameter);
                    const percentage = totalTrials ? Math.round((count / totalTrials) * 100) : 0;
                    return (
                      <li key={parameter} className="py-1 d-flex align-items-center justify-content-between gap-10">
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ background: GOAL_TRACK_PARAMETERS_COLORS_V2[parameter], width: "16px", height: "16px" }} />
                          <span className="fw-sbold" style={{ width: "25px", fontSize: 11 }}>{parameter}</span>
                          <p className="m-0" style={{ fontSize: 11 }}>{label}</p>
                        </div>
                        <span style={{ fontSize: 11 }}>{count}/{totalTrials} ({percentage}%)</span>
                      </li>
                    );
                  })}
                </ul>
              </Col>
            </Row>
          </div>
        </div>
      </Col>
      <Col xs="8" className="my-2 mt-3">
        <div className="cardCstm rounded-3">
          <div className="contentBody pb-5">
            <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center justify-content-between position-relative gap-10 ratingList-v2">
              {/* {Object.keys(GOAL_TRACK_PARAMETERS_V2).map((parameter, parameterIndex) => (
                <li key={parameter} className="px-2">
                  <SessionTrackingButton
                    element={parameter}
                    color={GOAL_TRACK_PARAMETERS_COLORS_V2[parameter]}
                    value={getCount(parameter)}
                    onValueChange={(_, __, selectedParameter, selectedValue) => setTrackingCount(selectedParameter, selectedValue)}
                    trackingIndex={parameterIndex}
                    isSessionTrail={false}
                    isDisabled={false}
                  />
                </li>
              ))} */}

               {Object.keys(GOAL_TRACK_PARAMETERS_V2).map((element, idx) => {
                          const color = GOAL_TRACK_PARAMETERS_COLORS_V2[element];
                          const fieldValue = values?.scores?.[element];
                          return (
                            <li key={element} className="px-2">
                              <SessionTrackingButton
                                isDisabled={false}
                                element={element}
                                color={GOAL_TRACK_PARAMETERS_COLORS_V2[element]}
                                value={fieldValue}
                                isSessionTrail={false}
                                onValueChange={(sessionIdx, trackingIdx, elem, val) => {
                                  let currentTrails = cloneDeep(values?.scores);
                                  currentTrails[element] = parseInt(val) || 0;
                                  setFieldValue(`scores`, currentTrails);
                                }}
                                trackingIndex={idx}
                              />
                            </li>
                          );
                        })}
            </ul>
          </div>
        </div>
        {/* <div className="py-2 mt-4">
          <GoalTrackingWatcher
            handleSave={() => saveGoalTrackings(trackings.filter((tracking) => tracking.dirty || (!tracking.id && tracking.tracked)))}
            isLoading={goalTrackLoading}
          >
            Save Goal Tracking
          </GoalTrackingWatcher>
        </div> */}
      </Col>
    </Row>
  );
};

const GoalTrackingForm = ({ index, goal, clientType, speechToText = false, innerRef, chartRef, saveGoalTrackings, updateGoalTracking, goalTrackLoading, removeGoal, editGoal, previewMode, autosaveTrigger }) => {

  const [showDropdown, setShowDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const hasInitializedNotes = useRef(false);
  const lastServerScores = useRef(null);
  const usesScoreTracker = SCORE_TRACKER_CLIENT_TYPES.includes(clientType);


  // Dropdown toggle handler
  const toggleDropdown = (e, index) => {
    e.stopPropagation();
    setShowDropdown(showDropdown === index ? null : index);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const formikInstance = innerRef?.current;
    if (!goal || !formikInstance) {
      return;
    }

    const currentValue = formikInstance.values?.goalTracking;

    const mergedTracking = map(
      range(Math.max(goal?.goalTracking?.length || 0, currentValue.length)),
      (index) => ({
        ...goal?.goalTracking?.[index],
        ...currentValue[index],
      })
    );
    // if (goal?.goal_notes) {
    //   formikInstance?.setFieldValue('goal_notes', goal?.goal_notes)
    // }

    // ✅ only set goal_notes ONCE
    if (!hasInitializedNotes.current) {
      formikInstance.setFieldValue('goal_notes', goal?.goal_notes || "");
      hasInitializedNotes.current = true;
    }

    //  formikInstance.setValues({
    //   goalTracking: mergedTracking,
    //   goal_notes: goal?.goal_notes || "",
    //   goal_attachementData: goal?.goal_attachementData ? JSON.parse(goal?.goal_attachementData) : [],
    // });
    formikInstance?.setFieldValue('goalTracking', mergedTracking)
    formikInstance?.setFieldValue('goal_attachementData', goal?.goal_attachementData ? JSON.parse(goal?.goal_attachementData) : [])
    formikInstance?.setFieldValue('show_tracker', goal?.show_tracker)

    // Keep unsaved score edits across refetches.
    const serverScores = goal?.scores || GOAL_TRACK_DEFAULT_VALUES_V2;
    const currentScores = formikInstance.values?.scores;
    if (!currentScores || isEqual(currentScores, lastServerScores.current)) {
      formikInstance.setFieldValue('scores', serverScores);
    }
    lastServerScores.current = serverScores;
  }, [goal]);

  const handleClickSaveTracking = (tracking) => {
    // get only new tracking that are not saved yet
    const newTrackings = tracking.filter(item => (!item.id && item.tracked));
    return saveGoalTrackings(newTrackings);
  }

  const handleDeleteGoal = async () => {
    const isConfirm = await confirmDialogV2({
      title: "DELETE AREA",
      message: "Are you sure you want to DELETE this area from your session report?"
    })
    if (!isConfirm) {
      return;
    }

    removeGoal(goal?.id)
  }

  return (
    <Formik
      innerRef={innerRef}
      initialValues={{
        goalTracking: [{ assesment_goal: goal?.id }],
        goal_notes: "",
        goal_attachementData: [],
        show_tracker: "show",
        hasUnsavedChanges: false,
      }}
      enableReinitialize
    >
      {({
        handleSubmit,
        setFieldValue,
        errors,
        values,
        getFieldMeta,
      }) => {
        return (
          <Form>
            <>
              <AutosaveWatcher
                goal={goal}
                autosaveTrigger={autosaveTrigger}
              />
              {/* <GoalTrackingWatcher /> */}
              <div className="goals goals-accordion" style={{ borderBottom: "0.75px solid #315D99", paddingBottom: "28px" }}>

                <Accordion.Item className="border-0" eventKey={index}>
                  <Accordion.Header>
                    <div className="w-100">
                      <div className="py-2" style={{ backgroundColor: "#F1F7FF", padding: "25px 10px 25px 13px" }}>
                        <div className="d-flex w-100 align-items-center justify-content-between pb-1 gap-2 mb-2" style={{ borderBottom: "1px solid #315D99" }}>
                          <div className="d-flex align-items-center  gap-2">
                            <div className="m-0 fw-bold proxima-regular text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>Area {index + 1}:</div>
                            <div className="m-0 text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>
                              {
                                goal?.assesmentGoalDisciplineArea
                                  ?.name
                              }
                            </div>
                          </div>
                          {
                            !previewMode && <div ref={dropdownRef}
                              className="cstm-dropdownclass">
                              <Dropdown show={showDropdown === index}>
                                <Dropdown.Toggle
                                  className="dropdown-custom-toggle"
                                  as="span" onClick={(e) => {
                                    e.stopPropagation();
                                  }} id={`dropdown-${index}`} >
                                  <img onClick={(e) => toggleDropdown(e, index)}
                                    width={19} height={18} src={threDots_img} alt="threDots_img" />
                                </Dropdown.Toggle>

                                <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                                  <Dropdown.Item onClick={() => editGoal(goal)}>Edit Area</Dropdown.Item>
                                  <Dropdown.Item onClick={handleDeleteGoal}>Delete Area</Dropdown.Item>
                                  <Dropdown.Divider></Dropdown.Divider>
                                  <Dropdown.Item onClick={() => setFieldValue('show_tracker', "show")} disabled={values?.show_tracker === "show"}>Add Trial Tracker</Dropdown.Item>
                                  <Dropdown.Item onClick={() => setFieldValue('show_tracker', "hidden")} disabled={values?.show_tracker !== "show"}>Remove Trial Tracker</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          }
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <div className="m-0 proxima-regular text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>Goal:</div>
                          <div className="m-0 text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>
                            {goal?.assesmentGoalDetail?.title}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="d-flex align-items-center gap-2 w-50">
                            <div className="m-0 proxima-regular text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>Context:</div>
                            <div className="m-0 text-uppercase" style={{ color: "#315D99", fontSize: 12 }}>
                              {goal?.assesmentGoalContext?.context}
                            </div>
                          </div>
                          <div className="py-2 content d-flex gap-2 w-50">
                            <div className="proxima-regular text-uppercase" style={{ color: "#315D99", fontSize: 12, marginTop: "1px" }}> Detail:</div>
                            <p className="m-0" style={{ color: "#315D99", fontSize: 12 }}>
                              {goal?.goal_description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className="p-0">
                    <div className="pt-3 ps-4">
                      <FieldArray name={`goalTracking`}>
                        {({ insert, remove, push }) => (
                          <>
                            {values?.show_tracker === "show" && (
                              <>
                                {(usesScoreTracker ? isTrialTrackerEmpty(values) : !values?.goalTracking?.some(x => x.id)) && <h6 style={{ color: "#f27e97" }}> <RequiredFieldAstrick /> Select atleast 1 Goal</h6>}
                                <h6
                                  style={{ fontSize: 12 }}
                                  className="m-0 date-title fw-bold text-uppercase themeBlue"
                                >
                                  TRIAL TRACKER
                                </h6>
                                {usesScoreTracker ? (
                                  <SchoolTrialTracker
                                    goal={goal}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    saveGoalTrackings={saveGoalTrackings}
                                    goalTrackLoading={goalTrackLoading}
                                  />
                                ) : (
                                  <Row>
                                    <Col lg="4" className="">
                                      <div
                                        className="cardCstm rounded-3"
                                      // style={{ background: "#F7F0F5" }}
                                      >
                                        <div className="contentBody">
                                          <Row>
                                            <Col lg="12" className="my-2">
                                              <SessionOutcomePieChart
                                                trackingData={
                                                  values?.goalTracking
                                                }
                                                // ref={chartRef}
                                                version={2}
                                              />
                                            </Col>
                                            <Col lg="12" className="my-2">
                                              {values?.goalTracking?.length > 0 ? (
                                                <ul className="list-unstyled ps-0 mb-0" style={{ maxWidth: "239px" }}>
                                                  {Object.entries(GOAL_TRACK_PARAMETERS_V2)?.map(([param, label], index) => {
                                                    let parameterPercentage = getGoalTrackingPercentage(param, values?.goalTracking);
                                                    const color = GOAL_TRACK_PARAMETERS_COLORS_V2[param];
                                                    return (
                                                      <li
                                                        key={index}
                                                        className="py-1 d-flex align-items-center justify-content-between gap-10"
                                                      >
                                                        <div className="d-flex align-items-center gap-1">
                                                          <div className="d-flex align-items-center gap-2">
                                                            <div style={{ background: color, width: "16px", height: "16px" }}></div>
                                                            <span className="fw-sbold" style={{ width: "25px", fontSize: 11 }}>
                                                              {param}
                                                            </span>{" "}
                                                          </div>
                                                          <p className="m-0" style={{ fontSize: 11 }}>
                                                            {label}
                                                          </p>
                                                        </div>
                                                        <p className="m-0" >
                                                          <span className="px-2" style={{ fontSize: 11 }}>
                                                            {
                                                              parameterPercentage
                                                            }
                                                          </span>
                                                          %
                                                        </p>
                                                      </li>
                                                    );
                                                  }
                                                  )}
                                                </ul>
                                              ) : (
                                                <p>
                                                  Goal not tracked yet
                                                </p>
                                              )}
                                            </Col>
                                          </Row>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col lg="8" className="my-2">
                                      <div className="">
                                        <div
                                          className="cardCstm rounded-3"
                                        // style={{
                                        //   background: "#F7F0F5",
                                        // }}
                                        >
                                          {/* <div className="top px-3 py-2 d-flex align-items-center justify-content-between border-bottom">
                                  <div className="d-flex align-items-center">
                                    <h6
                                      style={{ fontSize: 14 }}
                                      className="m-0 fw-bold text-uppercase"
                                    >
                                      Total Tracked
                                    </h6>
                                  </div>
                                  <CommonToolTip
                                    text="Lorem Ipsum Dolor Sit Amet"
                                    component={
                                      <Button
                                        className="border-0 p-0"
                                        variant="transparent"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                        >
                                          <path
                                            d="M9 18C10.78 18 12.5201 17.4722 14.0001 16.4832C15.4802 15.4943 16.6337 14.0887 17.3149 12.4442C17.9961 10.7996 18.1743 8.99002 17.8271 7.24419C17.4798 5.49836 16.6226 3.89471 15.364 2.63604C14.1053 1.37737 12.5016 0.520203 10.7558 0.172936C9.00998 -0.17433 7.20038 0.00389957 5.55585 0.685088C3.91131 1.36628 2.50571 2.51983 1.51677 3.99987C0.527841 5.47991 0 7.21997 0 9C0.00258081 11.3862 0.951621 13.6738 2.63889 15.3611C4.32616 17.0484 6.61384 17.9974 9 18ZM9 3.75C9.2225 3.75 9.44001 3.81598 9.62501 3.9396C9.81002 4.06322 9.95421 4.23892 10.0394 4.44448C10.1245 4.65005 10.1468 4.87625 10.1034 5.09448C10.06 5.31271 9.95283 5.51316 9.79549 5.6705C9.63816 5.82783 9.4377 5.93498 9.21948 5.97839C9.00125 6.02179 8.77505 5.99952 8.56948 5.91437C8.36391 5.82922 8.18821 5.68502 8.0646 5.50002C7.94098 5.31501 7.875 5.09751 7.875 4.875C7.875 4.57663 7.99352 4.29049 8.2045 4.07951C8.41548 3.86853 8.70163 3.75 9 3.75ZM8.25 7.5H9C9.39782 7.5 9.77935 7.65804 10.0607 7.93934C10.342 8.22065 10.5 8.60218 10.5 9V13.5C10.5 13.6989 10.421 13.8897 10.2803 14.0303C10.1397 14.171 9.94891 14.25 9.75 14.25C9.55109 14.25 9.36032 14.171 9.21967 14.0303C9.07902 13.8897 9 13.6989 9 13.5V9H8.25C8.05109 9 7.86032 8.92099 7.71967 8.78033C7.57902 8.63968 7.5 8.44891 7.5 8.25C7.5 8.05109 7.57902 7.86032 7.71967 7.71967C7.86032 7.57902 8.05109 7.5 8.25 7.5Z"
                                            fill="#3DA0AA"
                                          />
                                        </svg>
                                      </Button>
                                    }
                                  />
                                </div> */}
                                          <div className="contentBody pb-3">
                                            <ul className="list-unstyled ps-0 mb-0">
                                              {values?.goalTracking?.length > 0 &&
                                                values.goalTracking.map((tracking, trackingIndex) => {
                                                  const alreadyTracked = !!tracking.tracked;
                                                  const handleGoalTrackClick = (element) => {
                                                    const goalTrackingClone = cloneDeep(values?.goalTracking || []);
                                                    const existingRow = goalTrackingClone[trackingIndex];

                                                    existingRow.tracked = element;
                                                    if (tracking.id) {
                                                      updateGoalTracking(element, goal.id, tracking.id);
                                                    }

                                                    setFieldValue("goalTracking", goalTrackingClone);
                                                  };

                                                  const handleRemove = () => {
                                                    try {
                                                      remove(trackingIndex);
                                                    } catch (err) {
                                                      console.error("Error removing goal tracking: ", err);
                                                    }
                                                  };

                                                  return (
                                                    <li key={trackingIndex} className="border-bottom">
                                                      <Row className="w-100">
                                                        <Col lg={11} className="py-2">
                                                          <ul
                                                            className="list-unstyled ps-0 mb-0 d-flex align-items-center justify-content-between position-relative gap-10 ratingList-v2"
                                                            style={{ zIndex: 9 }}
                                                          >
                                                            {Object.keys(GOAL_TRACK_PARAMETERS_V2).map((element) => (
                                                              <li key={element} className="px-2">
                                                                <GoalTrackButton
                                                                  element={element}
                                                                  goalId={goal?.id}
                                                                  selected={tracking.tracked === element}
                                                                  onClick={() => handleGoalTrackClick(element)}
                                                                  color={GOAL_TRACK_PARAMETERS_COLORS_V2[element]}
                                                                />
                                                              </li>
                                                            ))}
                                                          </ul>
                                                        </Col>
                                                        <Col
                                                          lg={1}
                                                          className="d-flex align-items-center justify-content-end p-0 gap-10"
                                                        >
                                                          {trackingIndex !== 0 && values.goalTracking.length > 1 ? (
                                                            <Button
                                                              className="p-0 add-chart-graph-btn"
                                                              variant="transparent"
                                                              size="sm"
                                                              onClick={handleRemove}
                                                            >
                                                              <MinusBlackIcon color="#000" />
                                                            </Button>
                                                          ) : (
                                                            trackingIndex === 0 && (
                                                              <Button
                                                                className="p-0 add-chart-graph-btn"
                                                                variant="transparent"
                                                                size="sm"
                                                                onClick={() => push({ assesment_goal: goal.id })}
                                                              >
                                                                <PlusBlackIcon color="#3DA0AA" />
                                                              </Button>
                                                            )
                                                          )}
                                                        </Col>
                                                      </Row>
                                                    </li>
                                                  );
                                                })}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="py-2">
                                        <GoalTrackingWatcher
                                          handleSave={() => {
                                            return handleClickSaveTracking(values.goalTracking);
                                          }}
                                          isLoading={goalTrackLoading}
                                        >
                                          Save Goal Tracking
                                        </GoalTrackingWatcher>
                                      </div>
                                    </Col>
                                  </Row>
                                )}
                              </>
                            )}
                            <div className="py-2">
                              <h6
                                style={{ fontSize: 12 }}
                                className="m-0 date-title fw-bold text-uppercase themeBlue"
                              >
                                Goal Progression
                              </h6>
                              <Row className="my-2">
                                <Col lg='4'>
                                  <p style={{ fontStyle: "italic" }} className="themeBlue">
                                    Summarize qualitative and quantitative information on this goal and  indicate strategies that were used during this session. Also provide suggestions on places or contexts where this goal can be targeted.
                                  </p>
                                </Col>
                                <Col lg='8'>
                                  <CustomEditor speechToText={speechToText} previewMode={previewMode} name={`goal_notes`} attachmentName={`goal_attachementData`} />
                                </Col>
                              </Row>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
                <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: 999999999999 }} className="export-chart-copy">
                  <SessionOutcomePieChart
                    trackingData={
                      usesScoreTracker ? scoresToTrackingRows(values?.scores) : values?.goalTracking
                    }
                    ref={chartRef}
                    version={2}
                  />
                </div>
              </div>
            </>
          </Form >
        )
      }}
    </Formik >
  )
}

const AutosaveWatcher = ({ goal }) => {
  const { values } = useFormikContext();
  const { triggerAutosave } = useAutosaveReportContext();
  const timerRef = useRef(null);
  const lastAutosavedScores = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      // compare old vs new goal notes and scores (each scores change autosaves once)
      const scoresChanged = values?.scores
        && !isEqual(goal?.scores || GOAL_TRACK_DEFAULT_VALUES_V2, values.scores)
        && !isEqual(lastAutosavedScores.current, values.scores);
      if (goal?.goal_notes !== values?.goal_notes || scoresChanged) {
        console.log("Triggering autosave...");
        lastAutosavedScores.current = values?.scores;
        triggerAutosave();
      }
    }, 60000);

    return () => clearTimeout(timerRef.current);
  }, [goal?.goal_notes, values?.goal_notes, goal?.scores, values?.scores, triggerAutosave]);
};


export default GoalTrackingForm;
